/**
 * The browser half of a passkey ceremony (ADR-0051).
 *
 * The server speaks WebAuthn's JSON shapes (base64url strings);
 * `navigator.credentials` speaks ArrayBuffers. This converts both ways. It
 * uses the browser's own `parse...FromJSON` / `toJSON` when present and does
 * it by hand otherwise, because Safari on an older iPad has passkeys but not
 * those helpers.
 */

type Json = Record<string, any>;

const toB64url = (buf: ArrayBuffer | ArrayBufferView): string => {
  const bytes = buf instanceof ArrayBuffer
    ? new Uint8Array(buf)
    : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromB64url = (value: string): ArrayBuffer => {
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};

/** Whether this browser can use passkeys at all. */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential === 'function'
    && typeof navigator?.credentials?.create === 'function'
    && window.isSecureContext !== false;
}

const PKC = (): any => (typeof window !== 'undefined' ? (window as any).PublicKeyCredential : undefined);

function creationOptions(json: Json): PublicKeyCredentialCreationOptions {
  const native = PKC()?.parseCreationOptionsFromJSON;
  if (typeof native === 'function') return native.call(PKC(), json);
  return {
    ...json,
    challenge: fromB64url(json.challenge),
    user: { ...json.user, id: fromB64url(json.user.id) },
    excludeCredentials: (json.excludeCredentials ?? []).map((c: Json) => ({ ...c, id: fromB64url(c.id) })),
  } as PublicKeyCredentialCreationOptions;
}

function requestOptions(json: Json): PublicKeyCredentialRequestOptions {
  const native = PKC()?.parseRequestOptionsFromJSON;
  if (typeof native === 'function') return native.call(PKC(), json);
  return {
    ...json,
    challenge: fromB64url(json.challenge),
    allowCredentials: (json.allowCredentials ?? []).map((c: Json) => ({ ...c, id: fromB64url(c.id) })),
  } as PublicKeyCredentialRequestOptions;
}

function credentialToJSON(cred: PublicKeyCredential): Json {
  const native = (cred as any).toJSON;
  if (typeof native === 'function') {
    try { return native.call(cred); } catch { /* fall through to by-hand */ }
  }
  const r = cred.response as any;
  const response: Json = { clientDataJSON: toB64url(r.clientDataJSON) };
  if (r.attestationObject) {
    response.attestationObject = toB64url(r.attestationObject);
    if (typeof r.getTransports === 'function') response.transports = r.getTransports();
  } else {
    response.authenticatorData = toB64url(r.authenticatorData);
    response.signature = toB64url(r.signature);
    if (r.userHandle) response.userHandle = toB64url(r.userHandle);
  }
  return {
    id: cred.id,
    rawId: toB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: (cred as any).authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {},
    response,
  };
}

/** Ask the device to make a passkey for the server's registration options. */
export async function createPasskey(optionsJSON: Json): Promise<Json> {
  const cred = await navigator.credentials.create({ publicKey: creationOptions(optionsJSON) });
  if (!cred) throw new Error('No passkey was created');
  return credentialToJSON(cred as PublicKeyCredential);
}

/**
 * The autofill request waiting on the email field, if any. A browser holds
 * one passkey request at a time, so a modal request (the button) cancels it
 * first, and a new autofill request replaces the old one.
 */
let pendingAutofill: AbortController | null = null;

/** Stop offering passkeys in the email field's autofill. */
export function cancelPasskeyAutofill(): void {
  pendingAutofill?.abort();
  pendingAutofill = null;
}

/** Whether this browser can offer passkeys from a field's autofill list. */
export async function passkeyAutofillAvailable(): Promise<boolean> {
  try {
    const check = PKC()?.isConditionalMediationAvailable;
    return passkeysSupported() && typeof check === 'function' && (await check.call(PKC())) === true;
  } catch {
    return false;
  }
}

/** Whether the device has its own authenticator (Face ID, Touch ID, Windows Hello). */
export async function platformPasskeysAvailable(): Promise<boolean> {
  try {
    const check = PKC()?.isUserVerifyingPlatformAuthenticatorAvailable;
    return passkeysSupported() && typeof check === 'function' && (await check.call(PKC())) === true;
  } catch {
    return false;
  }
}

/**
 * Ask the device to sign in with a passkey for the server's sign-in options.
 *
 * `autofill` makes it a conditional request: nothing appears until the person
 * focuses a field marked `autocomplete="username webauthn"`, where their
 * passkeys join the autofill list. It waits until they pick one, or until
 * `cancelPasskeyAutofill()` or `signal` (either rejects it with an AbortError).
 */
export async function getPasskey(
  optionsJSON: Json,
  opts: { autofill?: boolean; signal?: AbortSignal } = {},
): Promise<Json> {
  const publicKey = requestOptions(optionsJSON);
  let cred: Credential | null;
  if (opts.autofill) {
    // A caller that has already given up must not cancel a newer request.
    if (opts.signal?.aborted) throw new DOMException('Passkey autofill cancelled', 'AbortError');
    cancelPasskeyAutofill();
    const controller = new AbortController();
    const forward = () => controller.abort();
    opts.signal?.addEventListener('abort', forward);
    pendingAutofill = controller;
    try {
      cred = await navigator.credentials.get({
        publicKey,
        mediation: 'conditional',
        signal: controller.signal,
      } as CredentialRequestOptions);
    } finally {
      opts.signal?.removeEventListener('abort', forward);
      if (pendingAutofill === controller) pendingAutofill = null;
    }
  } else {
    cancelPasskeyAutofill();
    cred = await navigator.credentials.get({ publicKey });
  }
  if (!cred) throw new Error('No passkey was chosen');
  return credentialToJSON(cred as PublicKeyCredential);
}

/**
 * What to tell the person when a ceremony fails in the browser, or null when
 * there is nothing to say: closing the passkey sheet (or letting it time out)
 * is the browser's NotAllowedError, and a person who cancelled needs no error.
 */
export function passkeyErrorMessage(err: unknown, action: 'add' | 'sign-in'): string | null {
  const name = (err as { name?: string })?.name;
  if (name === 'NotAllowedError' || name === 'AbortError') return null;
  if (name === 'InvalidStateError' && action === 'add') {
    return 'This device already has a passkey for your account.';
  }
  const serverMessage = (err as any)?.response?.data?.error;
  if (typeof serverMessage === 'string' && serverMessage) return serverMessage;
  return action === 'add' ? "That passkey couldn't be added. Try again." : "That passkey didn't work. Try again.";
}
